import type { Product } from '@bazaar/shared';

/**
 * Pure logic for the checkout QR scanner: payload normalisation, the
 * target-box hit-test, scan debouncing, and the add-to-cart decision rules.
 * Kept free of DOM/camera APIs so it can be unit tested directly; the camera
 * plumbing lives in `components/ScannerSheet.tsx`.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Normalises a decoded QR payload into a product code: trims whitespace,
 * extracts a `code` query param when the payload is a URL (or merely
 * contains one), and otherwise upper-cases the raw text. Product codes are
 * always compared upper-case (see `ProductCodeSchema` in `@bazaar/shared`).
 */
export function normalizeScanPayload(raw: string): string {
  const trimmed = raw.trim();

  try {
    const url = new URL(trimmed);
    const codeParam = url.searchParams.get('code');
    if (codeParam) return codeParam.trim().toUpperCase();
  } catch {
    // Not a full URL - fall through to the plain-text/`code=` cases below.
  }

  const match = /(?:^|[?&])code=([^&]+)/i.exec(trimmed);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1]).trim().toUpperCase();
    } catch {
      return match[1].trim().toUpperCase();
    }
  }

  return trimmed.toUpperCase();
}

/** Centroid of a set of points; `{0,0}` for an empty array. */
export function centroid(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / points.length, y: sum.y / points.length };
}

export function isPointInBox(point: Point, box: Box): boolean {
  return (
    point.x >= box.x && point.x <= box.x + box.width && point.y >= box.y && point.y <= box.y + box.height
  );
}

/**
 * Accepts a decode result only when the centroid of its bounding box /
 * corner points lands inside the target box. Codes on the printed sheet sit
 * close together, so this is what keeps the scanner from picking up a
 * neighbouring code that happens to be in the camera frame.
 */
export function isWithinTargetBox(points: Point[], targetBox: Box): boolean {
  if (points.length === 0) return false;
  return isPointInBox(centroid(points), targetBox);
}

/**
 * The centred square target box, in the coordinate space of the video's
 * native resolution (`videoWidth`/`videoHeight`) - the same space
 * `BarcodeDetector`/zxing report bounding boxes and corner points in.
 */
export function computeTargetBox(videoWidth: number, videoHeight: number, fraction = 0.6): Box {
  const side = Math.min(videoWidth, videoHeight) * fraction;
  return {
    x: (videoWidth - side) / 2,
    y: (videoHeight - side) / 2,
    width: side,
    height: side,
  };
}

/**
 * Debounces repeated reads of the *same* code within `windowMs`, while a
 * *different* code is always accepted immediately (e.g. scanning code A then
 * quickly code B, then A again).
 */
export class ScanDebouncer {
  private lastCode: string | null = null;
  private lastAcceptedAt = 0;

  constructor(private readonly windowMs = 1200) {}

  /** Returns whether `code` should be accepted at time `now` (ms), recording it if so. */
  accept(code: string, now: number): boolean {
    if (code === this.lastCode && now - this.lastAcceptedAt < this.windowMs) {
      return false;
    }
    this.lastCode = code;
    this.lastAcceptedAt = now;
    return true;
  }

  reset(): void {
    this.lastCode = null;
    this.lastAcceptedAt = 0;
  }
}

export type ScanOutcome =
  | { kind: 'added'; product: Product; qtyInCart: number }
  | { kind: 'out-of-stock'; product: Product }
  | { kind: 'unknown'; code: string };

/**
 * The same add-to-cart rules as tapping a product tile: cap at available
 * stock, and treat an unknown or inactive code as "unknown" rather than
 * exposing it as a distinct case (inactive products aren't sellable).
 */
export function classifyScannedProduct(
  product: Product | null,
  code: string,
  qtyAlreadyInCart: number,
): ScanOutcome {
  if (!product || !product.isActive) {
    return { kind: 'unknown', code };
  }
  if (product.stockQty <= 0 || qtyAlreadyInCart >= product.stockQty) {
    return { kind: 'out-of-stock', product };
  }
  return { kind: 'added', product, qtyInCart: qtyAlreadyInCart + 1 };
}

/**
 * The scan overlay's own state machine, independent of the camera/DOM
 * plumbing in `ScannerSheet.tsx`: `scanning` (camera or manual-entry
 * fallback live, looking for the next code) -> `confirmed` (a code was just
 * added; the choice panel is up and the camera is stopped) -> back to
 * `scanning` (via "Scan more items") or `closed` (via the × button,
 * "Proceed to checkout" or "Back to products").
 */
export type ScanPhase = 'scanning' | 'confirmed' | 'closed';

export interface ScanState {
  phase: ScanPhase;
  /** The most recent outcome, used to render the status line or the confirm panel. `null` before the first scan. */
  outcome: ScanOutcome | null;
}

export const initialScanState: ScanState = { phase: 'scanning', outcome: null };

export type ScanAction = { type: 'outcome'; outcome: ScanOutcome } | { type: 'scanMore' } | { type: 'close' };

/**
 * Pure reducer for the state machine above.
 *  - A successful add moves `scanning -> confirmed` and remembers the outcome.
 *  - An error (unknown/out-of-stock) outcome updates the status line but stays `scanning`.
 *  - Any outcome received while `confirmed` (or `closed`) is ignored outright -
 *    the choice panel is up (or the sheet is closing) and must not be
 *    silently replaced by a fresh scan, including one from the dev/test hook.
 *  - `scanMore` only has an effect from `confirmed`, returning to `scanning`
 *    with the outcome cleared so the status line resets to "Point at a code".
 *  - `close` always moves to `closed`.
 */
export function scanReducer(state: ScanState, action: ScanAction): ScanState {
  switch (action.type) {
    case 'outcome': {
      if (state.phase !== 'scanning') return state;
      if (action.outcome.kind === 'added') {
        return { phase: 'confirmed', outcome: action.outcome };
      }
      return { phase: 'scanning', outcome: action.outcome };
    }
    case 'scanMore':
      if (state.phase !== 'confirmed') return state;
      return { phase: 'scanning', outcome: null };
    case 'close':
      return { phase: 'closed', outcome: null };
    default:
      return state;
  }
}

declare global {
  interface Window {
    /**
     * Dev/test-only hook that feeds a decoded payload straight into the
     * scanner's handling logic, bypassing the camera and the target-box
     * hit-test. See `ScannerSheet.tsx` for why this is gated by
     * `?mockScanner=1` rather than `import.meta.env.MODE`.
     */
    __bazaarScanner?: { emit: (code: string) => void };
  }
}
